import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Heading,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import moment from 'moment'
import { Lab, UserLab, UserPanelItem } from '../../../types/bloodWork'
import PrintAccordion from './PrintAccordion'

export type PrintLabRecord = UserLab & {
  id: string
}

type LabResult = UserPanelItem['result'] | null | undefined

export type PrintLabPanel = {
  title: string
  rows: {
    title: string
    results: LabResult[]
  }[]
}

export type PrintLabMatrix = {
  dates: { id: string; label: string }[]
  panels: PrintLabPanel[]
}

export const hasLabResult = (result: LabResult) => {
  return typeof result === 'number' || String(result ?? '').trim().length > 0
}

export const formatLabDate = (record: PrintLabRecord) => {
  return moment(record.date.toDate()).format('YYYY-MM-DD')
}

export const formatLabTypeTitle = (title: string) => {
  const trimmedTitle = title.trim()

  return trimmedTitle && !/lab/i.test(trimmedTitle)
    ? `${trimmedTitle} Labs`
    : trimmedTitle
}

export const buildPrintLabMatrix = (
  template: Lab | undefined,
  records: PrintLabRecord[],
  onlyWithResults: boolean,
): PrintLabMatrix => {
  const sortedRecords = [...records].sort(
    (left, right) => right.date.toDate().getTime() - left.date.toDate().getTime(),
  )
  const panelOrder: string[] = []
  const rowOrder = new Map<string, string[]>()

  const addRow = (panelTitle: string, itemTitle: string) => {
    if (!rowOrder.has(panelTitle)) {
      panelOrder.push(panelTitle)
      rowOrder.set(panelTitle, [])
    }

    const rows = rowOrder.get(panelTitle)!
    if (!rows.includes(itemTitle)) {
      rows.push(itemTitle)
    }
  }

  template?.panels
    .filter((panel) => !panel.archived)
    .forEach((panel) => {
      panel.items
        .filter((item) => !item.archived)
        .forEach((item) => addRow(panel.title, item.title))
    })

  sortedRecords.forEach((record) => {
    record.panels.forEach((panel) => {
      panel.items.forEach((item) => {
        if (!template || hasLabResult(item.result)) {
          addRow(panel.title, item.title)
        }
      })
    })
  })

  const panels = panelOrder
    .map((panelTitle) => {
      const rows = (rowOrder.get(panelTitle) ?? [])
        .map((itemTitle) => {
          const results = sortedRecords.map((record) => {
            return record.panels
              .find((panel) => panel.title === panelTitle)
              ?.items.find((item) => item.title === itemTitle)?.result
          })

          return { title: itemTitle, results }
        })
        .filter(
          (row) =>
            !onlyWithResults || row.results.some((result) => hasLabResult(result)),
        )

      return { title: panelTitle, rows }
    })
    .filter((panel) => panel.rows.length > 0)

  return {
    dates: sortedRecords.map((record) => ({
      id: record.id,
      label: formatLabDate(record),
    })),
    panels,
  }
}

interface PrintLabResultsProps {
  expandAll: boolean
  labType: string
  onlyWithResults: boolean
  records: PrintLabRecord[]
  template?: Lab
}

const PrintLabResults = ({
  expandAll,
  labType,
  onlyWithResults,
  records,
  template,
}: PrintLabResultsProps) => {
  const matrix = buildPrintLabMatrix(template, records, onlyWithResults)

  return (
    <PrintAccordion expandAll={expandAll}>
      <AccordionItem>
        <AccordionButton>
          <Box flex='1' textAlign='left' fontWeight='bold'>
            {labType}
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>
          {matrix.panels.length === 0 ? (
            <Text>
              {onlyWithResults
                ? 'No lab test results with values.'
                : 'No lab tests available.'}
            </Text>
          ) : (
            <Stack spacing={5}>
              {matrix.panels.map((panel) => (
                <Box key={panel.title}>
                  <Heading
                    mb={2}
                    size='sm'
                    sx={{ '@media print': { breakAfter: 'avoid' } }}
                  >
                    {panel.title}
                  </Heading>
                  <TableContainer
                    overflowX='auto'
                    sx={{
                      '@media print': {
                        overflow: 'visible',
                        maxWidth: 'none',
                      },
                    }}
                  >
                    <Table
                      size='sm'
                      variant='simple'
                      sx={{
                        '@media print': {
                          fontSize: '8pt',
                          tableLayout: 'fixed',
                          width: '100%',
                        },
                        thead: {
                          display: 'table-header-group',
                        },
                        tr: {
                          breakInside: 'avoid',
                        },
                      }}
                    >
                      <Thead>
                        <Tr>
                          <Th width='30%'>Test</Th>
                          {matrix.dates.map((date) => (
                            <Th key={date.id} whiteSpace='nowrap'>
                              {date.label}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {panel.rows.map((row) => (
                          <Tr key={row.title}>
                            <Td whiteSpace='normal'>{row.title}</Td>
                            {row.results.map((result, index) => (
                              <Td key={matrix.dates[index].id}>
                                {hasLabResult(result) ? result : ''}
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Stack>
          )}
        </AccordionPanel>
      </AccordionItem>
    </PrintAccordion>
  )
}

export default PrintLabResults
